import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toast';
import type { FormExtrasContext } from '@/components/shared/crud-page';
import { useConfirm } from '@/hooks/use-confirm';
import { LookupField } from '@/components/shared/lookup-field';

interface StagedMaterial {
  material: string;
  materialName?: string;
  qty: number;
  cost?: number | null;
  unit?: string | null;          // Unit ref id (or null for none)
  unitName?: string;
}

interface PersistedMaterial {
  id: string;
  material?: { id: string; materialName?: string } | null;
  qty?: number | null;
  cost?: number | null;
  unit?: { id: string; name?: string } | string | null;
}

// Inline materials subform for the Job Card CRUD dialog. Mirrors the
// DispatchJobProductsSubform pattern:
//   - create mode  → stages rows in formValues._stagedMaterials, then the
//                    parent CrudConfig.afterSave POSTs them once the new
//                    JobCard id is known.
//   - edit mode    → loads the live JobMaterial rows from /job-materials
//                    and lets the user add / remove against the database.
export function JobCardMaterialsSubform({ isEdit, parentId, formValues, setFormValues }: FormExtrasContext) {
  const qc = useQueryClient();
  const confirm = useConfirm();

  const { data: materials = [] } = useQuery<any[]>({
    queryKey: ['/materials'],
    queryFn: async () => (await api<any[]>('/materials?limit=500')).data || [],
  });
  const { data: units = [] } = useQuery<any[]>({
    queryKey: ['/master-data/units'],
    queryFn: async () => (await api<any[]>('/master-data/units?limit=200')).data || [],
  });

  // Edit-mode: load persisted children. Defensive array coercion in case
  // a transient error response sneaks past `r.data || []`.
  const { data: persistedRaw } = useQuery<PersistedMaterial[]>({
    queryKey: ['/job-materials', parentId],
    queryFn: async () => {
      const r = await api<PersistedMaterial[]>(`/job-materials?jobCard=${parentId}&limit=200`);
      return Array.isArray(r.data) ? r.data : [];
    },
    enabled: isEdit && !!parentId,
  });
  const persisted: PersistedMaterial[] = Array.isArray(persistedRaw) ? persistedRaw : [];

  const addPersisted = useMutation({
    mutationFn: async (p: { material: string; qty: number; cost?: number | null; unit?: string | null }) => {
      const r = await api('/job-materials', {
        method: 'POST',
        body: JSON.stringify({
          jobCard: parentId,
          material: p.material,
          qty: p.qty,
          cost: p.cost ?? null,
          unit: p.unit ?? null,
        }),
      });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['/job-materials', parentId] }),
    onError: (e: any) => toast('Error', e.message, 'error'),
  });
  const delPersisted = useMutation({
    mutationFn: async (id: string) => {
      const r = await api(`/job-materials/${id}`, { method: 'DELETE' });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['/job-materials', parentId] }),
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  const staged: StagedMaterial[] = formValues._stagedMaterials ?? [];
  const setStaged = (next: StagedMaterial[]) => {
    setFormValues((prev) => ({ ...prev, _stagedMaterials: next }));
  };

  const [newMaterial, setNewMaterial] = React.useState('');
  const [newQty, setNewQty] = React.useState('');
  const [newCost, setNewCost] = React.useState('');
  const [newUnit, setNewUnit] = React.useState('');

  function clearAdd() {
    setNewMaterial(''); setNewQty(''); setNewCost(''); setNewUnit('');
  }

  function addRow() {
    if (!newMaterial || !newQty) return;
    const qty = Number(newQty);
    const cost = newCost === '' ? null : Number(newCost);
    if (isEdit && parentId) {
      addPersisted.mutate(
        { material: newMaterial, qty, cost, unit: newUnit || null },
        { onSuccess: clearAdd },
      );
      return;
    }
    const m = materials.find((x: any) => x.id === newMaterial);
    const u = units.find((x: any) => x.id === newUnit);
    setStaged([...staged, {
      material: newMaterial,
      materialName: m?.materialName,
      qty,
      cost,
      unit: newUnit || null,
      unitName: u?.name,
    }]);
    clearAdd();
  }

  // Normalise persisted row's `unit` to a display name — server may populate
  // it as a Unit document or leave it as a bare ObjectId string.
  function persistedUnitName(p: PersistedMaterial): string {
    if (!p.unit) return '';
    if (typeof p.unit === 'string') {
      return units.find((u: any) => u.id === p.unit)?.name ?? '';
    }
    return p.unit.name ?? '';
  }

  const rows: Array<{
    key: string;
    materialName?: string;
    qty?: number | null;
    cost?: number | null;
    unit?: string;
    onRemove: () => void;
  }> = isEdit
    ? persisted.map((p) => ({
        key: p.id,
        materialName: p.material?.materialName,
        qty: p.qty,
        cost: p.cost,
        unit: persistedUnitName(p),
        onRemove: async () => {
          if (await confirm({ title: 'Remove this material?', destructive: true, confirmLabel: 'Remove' })) {
            delPersisted.mutate(p.id);
          }
        },
      }))
    : staged.map((p, i) => ({
        key: `staged-${i}`,
        materialName: p.materialName,
        qty: p.qty,
        cost: p.cost ?? null,
        unit: p.unitName,
        onRemove: () => setStaged(staged.filter((_, j) => j !== i)),
      }));

  return (
    <div className="border-t pt-3">
      <div className="flex items-center justify-between mb-2">
        <Label>Materials ({rows.length})</Label>
      </div>
      {rows.length === 0 && (
        <div className="text-xs text-muted-foreground py-2">
          {isEdit ? 'No materials on this job.' : 'No materials added yet.'}
        </div>
      )}
      {rows.length > 0 && (
        <>
          {/* Mobile / tablet: stacked cards. */}
          <div className="lg:hidden space-y-2">
            {rows.map((r) => (
              <div key={r.key} className="border rounded-md p-3 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{r.materialName ?? '—'}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Qty: <span className="text-foreground">{r.qty ?? '—'}</span>
                    {r.unit ? <> · {r.unit}</> : null}
                    {typeof r.cost === 'number' ? <> · Cost: <span className="text-foreground">{r.cost.toFixed(2)}</span></> : null}
                  </div>
                </div>
                <button
                  type="button"
                  className="text-destructive text-lg leading-none px-2 py-1"
                  onClick={r.onRemove}
                  aria-label="Remove material"
                >×</button>
              </div>
            ))}
          </div>

          {/* Desktop: compact table. */}
          <table className="hidden lg:table w-full text-xs border rounded overflow-hidden">
            <thead className="bg-muted/40 text-[11px]">
              <tr>
                <th className="text-left px-2 py-1">Material</th>
                <th className="text-right px-2 py-1 w-16">Qty</th>
                <th className="text-left px-2 py-1 w-20">Unit</th>
                <th className="text-right px-2 py-1 w-20">Cost</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t">
                  <td className="px-2 py-1">{r.materialName ?? '—'}</td>
                  <td className="px-2 py-1 text-right">{r.qty ?? '—'}</td>
                  <td className="px-2 py-1">{r.unit ?? ''}</td>
                  <td className="px-2 py-1 text-right">{typeof r.cost === 'number' ? r.cost.toFixed(2) : ''}</td>
                  <td className="px-2 py-1">
                    <button type="button" className="text-destructive hover:underline text-xs" onClick={r.onRemove}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* Add-row controls. lg layout: [material 1fr] [qty 80] [unit 100] [cost 90] [add]. */}
      <div className="mt-3 grid grid-cols-1 gap-2 lg:mt-2 lg:grid-cols-[1fr_80px_100px_90px_auto] lg:gap-1">
        <LookupField
          endpoint="/materials"
          labelKey="materialName"
          value={newMaterial || null}
          onChange={(id) => setNewMaterial(id ?? '')}
          placeholder="Material…"
        />
        <div className="grid grid-cols-3 gap-2 lg:contents">
          <Input className="h-11 text-sm lg:h-8 lg:text-xs" type="number" step="0.01" placeholder="Qty" value={newQty} onChange={(e) => setNewQty(e.target.value)} />
          <LookupField
            endpoint="/master-data/units"
            labelKey="name"
            value={newUnit || null}
            onChange={(id) => setNewUnit(id ?? '')}
            placeholder="Unit"
          />
          <Input className="h-11 text-sm lg:h-8 lg:text-xs" type="number" step="0.01" placeholder="Cost" value={newCost} onChange={(e) => setNewCost(e.target.value)} />
        </div>
        <Button className="h-11 lg:h-8" type="button" disabled={!newMaterial || !newQty || addPersisted.isPending} onClick={addRow}>
          <Plus className="h-4 w-4 lg:h-3 lg:w-3" /> Add
        </Button>
      </div>
    </div>
  );
}

// afterSave hook companion — POSTs staged rows once the JobCard has an id.
export async function persistStagedJobMaterials(
  parentId: string,
  formValues: Record<string, any>,
  isCreate: boolean,
) {
  if (!isCreate) return;
  const staged: StagedMaterial[] = formValues._stagedMaterials ?? [];
  for (const p of staged) {
    const r = await api('/job-materials', {
      method: 'POST',
      body: JSON.stringify({
        jobCard: parentId,
        material: p.material,
        qty: p.qty,
        cost: p.cost ?? null,
        unit: p.unit ?? null,
      }),
    });
    if (!r.success) throw new Error(r.error?.message || 'Failed to add material');
  }
}
